import axios from "@/services/axios";
import store from "../store";

axios.interceptors.request.use(
  (config) => {
    if (store.state.token) {
      config.headers.Authorization = `Bearer ${store.state.token}`;
    }
    return config;
  },
  (err) => {
    return Promise.reject(err);
  }
);

// Handle errors and responses gracefully.
axios.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      switch (error.response.status) {
        case 401:
          store.commit("LOGOUT");
          break;
        case 500:
          console.log(error.response.statusText);
          break;
      }
    }

    return Promise.reject(error.response.data);
  }
);

export default {
  // Authentication API

  async login(email, password) {
    const response = await axios.post("/auth/login", {
      email: email,
      password: password
    });
    const token = response.data.token;

    if (token) {
      store.commit("LOGIN", token);
    }
  },
  // check for existing cookie session on server
  async userProfile() {
    try {
      const response = await axios.get("/auth/profile");
      const user = await response.data;

      return user;
    } catch (err) {
      return false;
    }
  },

  async launchPoll(id) {
    const response = await axios.get("/polls/launchPoll/" + id);
    return response.data;
  },
  async closePoll(id) {
    const response = await axios.get("/polls/closePoll/" + id);
    return response.data;
  },
  async getPolls(eventId) {
    const response = await axios.get(`/polls/event/${eventId}`);
    return response.data;
  },
  async getPoll(pollId) {
    const response = await axios.get(`/polls/${pollId}`);
    return response.data;
  },

  async clonePoll(id) {
    const response = await axios.post("/polls/clone/" + id);
    return response.data;
  },

  async savePoll(id, data) {
    const response = await axios.put("/polls/" + id, data);
    return response.data;
  },

  async updatePoll(id, data) {
    const response = await axios.patch("/polls/" + id, data);
    return response.data;
  },

  async createPoll(poll) {
    const response = await axios.post("/polls", poll);
    return response.data;
  },

  async deletePoll(pollId) {
    const response = await axios.delete(`/polls/${pollId}`);
    return response;
  },
  async reorderPolls(orderArray) {
    const response = await axios.post("/polls/reorder/", orderArray);
    return response;
  },

  /**
   * User API
   */
  async getUser(id) {
    const response = await axios.get("/users/" + id);
    return response.data;
  },
  async getUserByToken(eventId, token) {
    const response = await axios.get(`/users/token/${eventId}/${token}`);
    return response.data;
  },
  async generateToken(userId) {
    const response = await axios.get(`/users/generateToken/${userId}`);
    return response.data.token;
  },

  async getUsers(eventId, params) {
    const response = await axios.get(`/users/event/${eventId}/`, {
      params: {
        ...params
      }
    });
    return response.data;
  },
  async getUserCount(eventId, search) {
    const response = await axios.get(`/users/count/${eventId}/`, {
      params: {
        search
      }
    });
    return response.data.count;
  },
  async getProxies(userId) {
    const response = await axios.get("/users/proxies/" + userId);
    return response.data;
  },
  async createUser(eventId, user) {
    const response = await axios.post(`/users/create/${eventId}`, user);
    return response.data;
  },
  async saveUser(userId, body) {
    const response = await axios.put("/users/update/" + userId, body);
    return response.data;
  },
  async deleteUser(user) {
    const response = await axios.delete("/users/" + user._id);
    return response.data;
  },
  async deleteUserByIds(ids) {
    const response = await axios.post("/users/delete", { ids: ids });
    return response.data;
  },

  /**
   * Voting API
   */
  async getVotes(eventId) {
    const response = await axios.get(`/votes/${eventId}`);
    return response.data;
  },

  async getVotesByUser(userId) {
    const response = await axios.get("/votes/user/" + userId);
    return response.data;
  },

  // returns any votes if found for the user
  async checkVote(pollId) {
    const response = await axios.get("/votes/history/" + pollId);
    return response.data;
  },

  async getVoteTotal(pollId) {
    const response = await axios.get(`/votes/getVoteTotal/${pollId}`);
    return response.data;
  },

  async deleteVote(userId, pollId) {
    const response = await axios.delete(`/votes/${userId}/${pollId}`);
    return response.data;
  },

  async clearResponses(pollId) {
    const response = await axios.get("/votes/clearResponses/" + pollId);
    return response.data;
  },

  async saveVote(data) {
    const response = await axios.post("/votes", data);
    return response.data;
  },

  async updateVote(data) {
    const response = await axios.put("/votes/" + data.userId, data);
    return response.data;
  },

  // Notifications
  async sendTokenEmail(data) {
    const response = await axios.post("/notifications/sendToken", data);
    return response.data;
  },

  // Settings

  getSettings: async function() {
    const response = await axios.get("/settings");
    return response.data;
  },

  saveSettings: async function(settings) {
    return await axios.post("/settings", settings);
  },

  getDomains: async function() {
    const response = await axios.get("/settings/domains");
    return response.data;
  },
  saveDomains: async function(domains) {
    const response = await axios.post("/settings/domains", domains);
    return response.data;
  },

  // Events

  async getEvent(eventId) {
    const response = await axios.get("/events/" + eventId);
    const event = response.data;
    return event;
  },

  async getEvents() {
    let response = await axios.get("/events");
    const events = response.data;
    return events;
  },

  async updateEvent(eventId, data) {
    await axios.put("/events/" + eventId, data);
  },

  async createEvent(data) {
    const response = await axios.post("/events", data);
    const event = response.data;
    return event;
  },

  async deleteEvent(eventId) {
    await axios.delete("/events/" + eventId);
  },

  async getArchivedEvents() {
    let response = await axios.get("/events/archived");
    const events = response.data;
    return events;
  },

  async deleteArchivedEvent(eventId) {
    await axios.delete("/events/archived/delete/" + eventId);
  },

  async restoreArchivedEvent(eventId) {
    await axios.post("/events/archived/restore/" + eventId);
  },

  // Global Admin

  async getManagers() {
    let response = await axios.get("/auth/managers");
    const managers = response.data;
    return managers;
  },

  async saveManager(manager) {
    await axios.post("/auth/managers/create", manager);
  },

  async deleteManager(id) {
    await axios.post("/auth/managers/delete/" + id);
  },

  // EXPORTING

  async exportReport(eventId) {
    const response = await axios.get(`/votes/export/${eventId}`);
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `agmify-${eventId}-${timestamp}-users.csv`;

    const fileURL = window.URL.createObjectURL(new Blob([response.data]));
    var fileLink = document.createElement("a");
    fileLink.href = fileURL;

    fileLink.setAttribute("download", filename);
    document.body.appendChild(fileLink);
    fileLink.click();
  },
  async exportUsers(eventId) {
    const response = await axios.get(`/users/export/${eventId}`);
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `agmify-${eventId}-${timestamp}-votes.csv`;

    const fileURL = window.URL.createObjectURL(new Blob([response.data]));
    var fileLink = document.createElement("a");
    fileLink.href = fileURL;

    fileLink.setAttribute("download", filename);
    document.body.appendChild(fileLink);
    fileLink.click();
  }
};
